import dayjs, { Dayjs } from "dayjs";
import { z } from "zod";
import { refinements } from "@helpers/refinements";
import { ArrayElementType } from "@helpers/utils";
import i18n from "@/i18n";

const ORDER_PRICE_PRECISION = import.meta.env.VITE_ORDER_PRICE_PRECISION;

const maxDecimalPrecisionRefinement = refinements.maxDecimalPrecision(ORDER_PRICE_PRECISION);

const validityDatesScheme = {
    valid_from: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),
    valid_to: z.instanceof(dayjs as unknown as typeof Dayjs).nullish(),
};

const productItemScheme = z.object({
    type: z.literal("product"),
    product: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
    }),
    sale_price: z.coerce
        .number()
        .min(0)
        .refine(maxDecimalPrecisionRefinement.refine, maxDecimalPrecisionRefinement.message),
    ...validityDatesScheme,
});

const categoryItemScheme = z.object({
    type: z.literal("category"),
    category: z.object({
        id: z.number(),
        name: z.string(),
        code: z.string(),
    }),
    ...validityDatesScheme,
});

export const customerGroupItemScheme = z
    .discriminatedUnion("type", [productItemScheme, categoryItemScheme])
    .superRefine((item, ctx) => {
        if (item.valid_from && item.valid_to && dayjs(item.valid_to).isBefore(item.valid_from)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                params: {
                    i18n: "general.validation.global.compareDate",
                },
                path: ["valid_to"],
            });
        }
    });

export const customerGroupsScheme = z.object({
    code: z.string().trim().optional(),
    name: z.string().trim(),
    discount: z.coerce.number().min(0).max(100),

    customers: z
        .array(
            z.object({
                value: z.object({
                    id: z.number(),
                    name: z.string(),
                    code: z.string(),
                }),
            })
        )
        .min(1, {
            message: i18n.t("customerGroups.mutation.validation.noCustomersAdded"),
        }),

    items: z.array(customerGroupItemScheme).min(1, {
        message: i18n.t("customerGroups.mutation.validation.noItemsAdded"),
    }),
});

export type CustomerGroupsFormTyping = z.infer<typeof customerGroupsScheme>;
export type CustomerItem = ArrayElementType<CustomerGroupsFormTyping["customers"]>;
export type DiscountItem = ArrayElementType<CustomerGroupsFormTyping["items"]>;
export type ProductItem = Extract<DiscountItem, { type: "product" }>;
export type CategoryItem = Extract<DiscountItem, { type: "category" }>;

export interface Customer {
    id: number;
    name: string;
}
